import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Tabs,
  Table,
  Badge,
  Row,
  Col,
  Statistic,
  Timeline,
  Spin,
  message,
  Popconfirm,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
} from "antd";
import {
  EditOutlined,
  ArrowLeftOutlined,
  ToolOutlined,
  UserOutlined,
  HistoryOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import {
  getMachine,
  getMaintenanceLogs,
  getMachineOperators,
  getMachineUtilization,
  logMaintenance,
  completeMaintenance,
} from "../../api/machineApi";
import PageTitle from "../page-header/PageHeader";
import moment from "moment";

const { TabPane } = Tabs;
const { Option } = Select;

const MACHINE_TYPES = {
  corrugation: "Corrugation",
  die_cutting: "Die Cutting (Casting)",
  rotary: "Rotary",
  offset: "Offset Printing",
  flexo: "Flexo Printing",
  pasting: "Pasting",
  sheet_cutter: "Sheet Cutter",
  stitching: "Stitching",
  glueing: "Glueing",
  lamination: "Lamination",
};

const STATUS_CONFIG = {
  operational: { color: "green", icon: <CheckCircleOutlined />, text: "Operational" },
  maintenance: { color: "orange", icon: <WarningOutlined />, text: "Under Maintenance" },
  breakdown: { color: "red", icon: <CloseCircleOutlined />, text: "Breakdown" },
};

const MachineDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [operators, setOperators] = useState([]);
  const [utilization, setUtilization] = useState(null);
  const [maintenanceModal, setMaintenanceModal] = useState(false);
  const [maintenanceForm] = Form.useForm();

  useEffect(() => {
    fetchMachine();
    fetchMaintenanceLogs();
    fetchOperators();
    fetchUtilization();
  }, [id]);

  const fetchMachine = async () => {
    try {
      setLoading(true);
      const data = await getMachine(id);
      setMachine(data);
    } catch (error) {
      message.error("Failed to fetch machine details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceLogs = async () => {
    try {
      const data = await getMaintenanceLogs(id, { limit: 20 });
      setMaintenanceLogs(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error("Failed to fetch maintenance logs", error);
    }
  };

  const fetchOperators = async () => {
    try {
      const data = await getMachineOperators(id);
      setOperators(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error("Failed to fetch operators", error);
    }
  };

  const fetchUtilization = async () => {
    try {
      const data = await getMachineUtilization(id, {
        start_date: moment().subtract(30, "days").format("YYYY-MM-DD"),
        end_date: moment().format("YYYY-MM-DD"),
      });
      setUtilization(data);
    } catch (error) {
      console.error("Failed to fetch utilization", error);
    }
  };

  const handleLogMaintenance = async () => {
    try {
      const values = await maintenanceForm.validateFields();
      await logMaintenance(id, {
        ...values,
        started_at: values.started_at.toISOString(),
      });
      message.success("Maintenance logged successfully");
      setMaintenanceModal(false);
      maintenanceForm.resetFields();
      fetchMaintenanceLogs();
    } catch (error) {
      message.error("Failed to log maintenance");
    }
  };

  const handleCompleteMaintenance = async (logId) => {
    try {
      await completeMaintenance(id, logId);
      message.success("Maintenance marked as complete");
      fetchMaintenanceLogs();
    } catch (error) {
      message.error("Failed to complete maintenance");
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!machine) {
    return (
      <Card>
        <p>Machine not found</p>
        <Button onClick={() => navigate("/machine")}>Back to List</Button>
      </Card>
    );
  }

  const statusConfig = STATUS_CONFIG[machine.status] || STATUS_CONFIG.operational;

  const maintenanceColumns = [
    {
      title: "Date",
      dataIndex: "started_at",
      render: (date) => moment(date).format("DD MMM YYYY HH:mm"),
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (type) => <Tag>{type}</Tag>,
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "completed" ? "green" : "orange"}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) =>
        record.status !== "completed" && (
          <Popconfirm
            title="Mark as completed?"
            onConfirm={() => handleCompleteMaintenance(record.id)}
          >
            <Button size="small" type="primary">
              Complete
            </Button>
          </Popconfirm>
        ),
    },
  ];

  const operatorColumns = [
    {
      title: "Name",
      dataIndex: ["user", "username"],
    },
    {
      title: "Skill Level",
      dataIndex: "skill_level",
      render: (level) => <Tag color="blue">{level || "Standard"}</Tag>,
    },
    {
      title: "Primary",
      dataIndex: "is_primary",
      render: (isPrimary) =>
        isPrimary ? <Tag color="green">Primary</Tag> : <Tag>Secondary</Tag>,
    },
    {
      title: "Assigned Since",
      dataIndex: "assigned_at",
      render: (date) => moment(date).format("DD MMM YYYY"),
    },
  ];

  return (
    <>
      <PageTitle title="Back" subtitle={`Machine: ${machine.name}`} />

      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/machine")}>
          Back to List
        </Button>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/machine/${id}/edit`)}
        >
          Edit Machine
        </Button>
        <Button
          icon={<ToolOutlined />}
          onClick={() => setMaintenanceModal(true)}
        >
          Log Maintenance
        </Button>
      </Space>

      {/* Overview Card */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <Descriptions
              title={
                <Space>
                  <span>{machine.name}</span>
                  <Tag color={statusConfig.color}>
                    {statusConfig.icon} {statusConfig.text}
                  </Tag>
                </Space>
              }
              bordered
              column={{ xs: 1, sm: 2, md: 3 }}
            >
              <Descriptions.Item label="Code">{machine.code}</Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag color="blue">{MACHINE_TYPES[machine.type] || machine.type}</Tag>
              </Descriptions.Item>
              {machine.subtype && (
                <Descriptions.Item label="Subtype">
                  <Tag color="geekblue">{machine.subtype.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</Tag>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Inhouse / Outsource">
                {machine.is_inhouse ? (
                  <Tag color="green">Inhouse</Tag>
                ) : (
                  <Tag color="orange">Outsourced</Tag>
                )}
              </Descriptions.Item>
              {machine.is_inhouse ? (
                <Descriptions.Item label="Location">
                  {machine.location || "N/A"}
                </Descriptions.Item>
              ) : (
                <Descriptions.Item label="Vendor">
                  {machine.vendor ? (
                    <Space direction="vertical" size={0}>
                      <strong>{machine.vendor.name}</strong>
                      {machine.vendor.phone && <span>{machine.vendor.phone}</span>}
                    </Space>
                  ) : (
                    "No vendor linked"
                  )}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Width">
                {machine.width ? `${machine.width}"` : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Height">
                {machine.height ? `${machine.height}"` : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Unit">{machine.unit || "inches"}</Descriptions.Item>
              <Descriptions.Item label="Speed (Sheets/hr)">
                {machine.speed_sheets_per_hour || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Speed (m/min)">
                {machine.speed_meters_per_min || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Manufacturer">
                {machine.manufacturer || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Model">
                {machine.model_number || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Purchase Date">
                {machine.purchase_date
                  ? moment(machine.purchase_date).format("DD MMM YYYY")
                  : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Compatible Materials" span={3}>
                {machine.compatible_materials?.length > 0 ? (
                  machine.compatible_materials.map((m) => (
                    <Tag key={m}>{m}</Tag>
                  ))
                ) : (
                  "N/A"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Compatible Flutes" span={3}>
                {machine.compatible_flutes?.length > 0 ? (
                  machine.compatible_flutes.map((f) => (
                    <Tag key={f} color="purple">
                      {f}
                    </Tag>
                  ))
                ) : (
                  "N/A"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Applicable Stages" span={3}>
                {machine.applicable_stages?.length > 0 ? (
                  machine.applicable_stages.map((s) => (
                    <Tag key={s} color="cyan">
                      {s}
                    </Tag>
                  ))
                ) : (
                  "N/A"
                )}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} lg={8}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Total Run Hours"
                  value={machine.total_run_hours || 0}
                  suffix="hrs"
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Jobs Completed"
                  value={machine.total_jobs_completed || 0}
                />
              </Col>
              <Col span={24}>
                <Card size="small" title="Maintenance Info">
                  <p>
                    <strong>Last Maintenance:</strong>{" "}
                    {machine.last_maintenance
                      ? moment(machine.last_maintenance).format("DD MMM YYYY")
                      : "Never"}
                  </p>
                  <p>
                    <strong>Next Maintenance:</strong>{" "}
                    {machine.next_maintenance ? (
                      <Tag
                        color={
                          moment(machine.next_maintenance).isBefore(moment())
                            ? "red"
                            : "green"
                        }
                      >
                        {moment(machine.next_maintenance).format("DD MMM YYYY")}
                      </Tag>
                    ) : (
                      "Not scheduled"
                    )}
                  </p>
                  <p>
                    <strong>Interval:</strong>{" "}
                    {machine.maintenance_interval_days || 30} days
                  </p>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      {/* Tabs for additional info */}
      <Card>
        <Tabs defaultActiveKey="maintenance">
          <TabPane
            tab={
              <span>
                <HistoryOutlined /> Maintenance Logs
              </span>
            }
            key="maintenance"
          >
            <Table
              columns={maintenanceColumns}
              dataSource={maintenanceLogs}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                <UserOutlined /> Operators
              </span>
            }
            key="operators"
          >
            <Table
              columns={operatorColumns}
              dataSource={operators}
              rowKey="id"
              pagination={false}
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                <BarChartOutlined /> Utilization
              </span>
            }
            key="utilization"
          >
            {utilization ? (
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Utilization Rate"
                    value={utilization.utilization_rate || 0}
                    suffix="%"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Scheduled Hours"
                    value={utilization.scheduled_hours || 0}
                    suffix="hrs"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Jobs This Month"
                    value={utilization.jobs_count || 0}
                  />
                </Col>
              </Row>
            ) : (
              <p>No utilization data available</p>
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* Maintenance Modal */}
      <Modal
        title="Log Maintenance Activity"
        open={maintenanceModal}
        onOk={handleLogMaintenance}
        onCancel={() => {
          setMaintenanceModal(false);
          maintenanceForm.resetFields();
        }}
        okText="Log Maintenance"
      >
        <Form form={maintenanceForm} layout="vertical">
          <Form.Item
            name="type"
            label="Maintenance Type"
            rules={[{ required: true, message: "Please select type" }]}
          >
            <Select placeholder="Select type">
              <Option value="preventive">Preventive</Option>
              <Option value="corrective">Corrective</Option>
              <Option value="emergency">Emergency</Option>
              <Option value="inspection">Inspection</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input.TextArea rows={3} placeholder="Describe the maintenance activity" />
          </Form.Item>
          <Form.Item
            name="started_at"
            label="Start Date/Time"
            rules={[{ required: true, message: "Please select date" }]}
          >
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="cost" label="Cost (Optional)">
            <Input type="number" prefix="₹" placeholder="0.00" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MachineDetail;
